import { ReactComponent as UpArrow } from '../assets/up-arrow.svg';
import { ReactComponent as DownArrow } from '../assets/down-arrow.svg';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getEventTranslation } from '../utils/eventTranslation';
import { formatDateRange } from '../utils/dateUtils';

const EventTable = ({
  events,
  setDeleteModal,
  setUpdateModal,
  loading,
  setAddEventModal,
  setViewContentModal,
  setViewEvent,
}) => {
  const [sortedEvents, setSortedEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  useEffect(() => {
    if (!events?.length) return;
    const sorted = [...events].sort((a, b) => new Date(b.date) - new Date(a.date));
    setSortedEvents(sorted);
  }, [events]);

  // For searching the event by name
  const displayedEvents = useMemo(
    () => sortedEvents.filter((e) => e.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [sortedEvents, searchTerm]
  );

  const handleSort = (key, ord) => {
    setSortedEvents((prev) => {
      const copy = [...prev];
      copy.sort((a, b) => {
        let cmp = 0;
        switch (key) {
          case 'name':
            cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            break;
          case 'date':
            cmp = new Date(a.date) - new Date(b.date);
            break;
          case 'location':
            cmp = a.location.toLowerCase().localeCompare(b.location.toLowerCase());
            break;
        }
        return ord === 'asc' ? cmp : -cmp;
      });
      return copy;
    });
  };

  const formatDate = (dateString) => {
    // Parse the date string directly to avoid timezone shifts
    const dateStr = dateString.split('T')[0]; // Get YYYY-MM-DD part
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="overflow-x-auto flex flex-col gap-5">
      <div className="flex justify-between items-center mt-5 px-5">
        <input
          type="text"
          placeholder="Search events by name..."
          className="border border-gray-300 rounded-md py-2 px-4 w-64 focus:outline-none focus:ring-2 focus:ring-green-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button
          className="bg-[#2E7A32] hover:bg-green-500 text-white font-bold py-1 px-3 rounded-full"
          onClick={() => setAddEventModal(true)}
        >
          Add Event +
        </button>
      </div>
      <table className="min-w-full table-auto border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
              <div className="flex items-center justify-between">
                <span>Name</span>
                <div className="flex space-x-2">
                  <UpArrow
                    width={15}
                    height={15}
                    onClick={() => handleSort('name', 'asc')}
                    className="hover:cursor-pointer"
                  />
                  <DownArrow
                    width={15}
                    height={15}
                    onClick={() => handleSort('name', 'desc')}
                    className="hover:cursor-pointer"
                  />
                </div>
              </div>
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
              <div className="flex items-center justify-between">
                <span>Date</span>
                <div className="flex space-x-2">
                  <UpArrow
                    width={15}
                    height={15}
                    onClick={() => handleSort('date', 'asc')}
                    className="hover:cursor-pointer"
                  />
                  <DownArrow
                    width={15}
                    height={15}
                    onClick={() => handleSort('date', 'desc')}
                    className="hover:cursor-pointer"
                  />
                </div>
              </div>
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
              Time
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
              <div className="flex items-center justify-between">
                <span>Location</span>
                <div className="flex space-x-2">
                  <UpArrow
                    width={15}
                    height={15}
                    onClick={() => handleSort('location', 'asc')}
                    className="hover:cursor-pointer"
                  />
                  <DownArrow
                    width={15}
                    height={15}
                    onClick={() => handleSort('location', 'desc')}
                    className="hover:cursor-pointer"
                  />
                </div>
              </div>
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left text-[#2E7A32] font-bold">
              Options
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className="text-center py-4">
                Loading...
              </td>
            </tr>
          ) : (
            displayedEvents.map((event) => {
              const eventName = getEventTranslation(event, 'name', currentLang);
              const eventSubHeader = getEventTranslation(event, 'subHeader', currentLang);

              return (
                <tr
                  key={event._id}
                  className="bg-white hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setViewContentModal(true);
                    setViewEvent(event);
                  }}
                >
                  <td className="border border-gray-300 px-4 py-2 flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img src={event.image} alt={eventName} className="w-24 h-24 object-cover" />
                    </div>
                    <div className="space-y-2">
                      <div className="font-semibold">{eventName}</div>
                      <div className="text-gray-500 text-sm">{eventSubHeader}</div>
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {formatDateRange(event, currentLang)}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">{event.time}</td>
                  <td className="border border-gray-300 px-4 py-2">{event.location}</td>
                  <td className="border border-gray-300 px-4 py-2 capitalize">
                    <div className="flex justify-around">
                      <button
                        className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded-md"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUpdateModal(true);
                          setViewEvent(event);
                        }}
                      >
                        Update
                      </button>
                      <button
                        className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded-md"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal(true);
                          setViewEvent(event);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export { EventTable };
